import { useContext, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Modal from '@mui/material/Modal'
import CircularProgress from '@mui/material/CircularProgress'
import styles from './styles.module.css'
import PrimaryButton from '../../../global/components/buttons/primary-button/PrimaryButton'
import SecondaryButton from '../../../global/components/buttons/secondary-button/SecondaryButton'
import { ToastNotificationContext } from '../../../global/context/toast-context/ToastNotificationContext'
import { fetchData } from '../../../global/utils/fetch'
import {
  getIncidentReportByObjectId,
  getIncidentReportsByOrderObjectId,
  createIncidentReport,
  updateIncidentReport,
  getTechnicianAssignedToOrder,
  getIncidentReportImagesAsBase64,
  getOrderImagesAsBase64,
} from '../../api'
import { generateIncidentReportPdf } from '../../utils/generate-pdf'
import OverviewTab from './tabs/OverviewTab'
import InvestigationTab from './tabs/InvestigationTab'
import RootCauseTab from './tabs/RootCauseTab'
import RecommendationsTab from './tabs/RecommendationsTab'
import ActionPlanTab from './tabs/ActionPlanTab'
import OrderDetailsPanel from './OrderDetailsPanel'
import { IAccidentData } from '../../../global/constants/incident-report'
import { IOrder } from '../../../../../app/entities/Order'
import { ITechnician } from '../../../../../app/entities/Technician'

const TAB_BUTTON_STYLE = { width: 'max-content', height: 'max-content' }

export interface IIncidentReportFormData {
  objectId?: string
  incidentDate: string
  technicianObjectId?: string
  orderObjectId?: string
  vanObjectId?: string
  installationPointObjectId?: string
  coachObjectId?: string
  isLostTimeInjury: boolean
  reviewChannel: string
  reviewScore: number | null
  incidentType: string
  incidentOverviewAndResolution: string
  severityOfIncident: string
  investigationSummary: string
  employeeStatement: string
  cost: number | null
  qualifyingDefects: boolean
  dtClaims: boolean
  dtClaimsNumber: string
  wheelOff: boolean
  wheelOffReason: string
  isAccident: boolean
  accidentData: IAccidentData
  rootCauseAnalysis: string
  recommendations: string
  correctionActionPlan: string
  customerMileageAtIncident: number | null
  selectedImageObjectIds: string[]
  workOrderId: string
  status: string
}

const DEFAULT_FORM_DATA: IIncidentReportFormData = {
  incidentDate: '',
  isLostTimeInjury: false,
  reviewChannel: '',
  reviewScore: null,
  incidentType: '',
  incidentOverviewAndResolution: '',
  severityOfIncident: '',
  investigationSummary: '',
  employeeStatement: '',
  cost: null,
  qualifyingDefects: false,
  dtClaims: false,
  dtClaimsNumber: '',
  wheelOff: false,
  wheelOffReason: '',
  isAccident: false,
  accidentData: {
    driverName: '',
    cityState: '',
    numberOfInjuries: 0,
    numberOfFatalities: 0,
    isVehicleTowed: false,
    isHazmatRelease: false,
    isDriverCitation: false,
  },
  rootCauseAnalysis: '',
  recommendations: '',
  correctionActionPlan: '',
  customerMileageAtIncident: null,
  selectedImageObjectIds: [],
  workOrderId: '',
  status: '',
}

type TabType = 'overview' | 'investigation' | 'rootCause' | 'recommendations' | 'actionPlan'

interface IIncidentReportModalProps {
  isOpen: boolean
  handleClosed: () => void
  incidentReportObjectId?: string
  orderObjectId?: string
  orderData?: IOrder
  onSaveSuccess?: () => void
  defaultValues?: {
    cost?: number | null
    incidentOverviewAndResolution?: string
    wheelOff?: boolean
  }
}

function IncidentReportModal({
  isOpen,
  handleClosed,
  incidentReportObjectId,
  orderObjectId,
  orderData,
  onSaveSuccess,
  defaultValues,
}: IIncidentReportModalProps) {
  const { toastStatus, setToastStatus } = useContext(ToastNotificationContext)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [activeTab, setActiveTab] = useState<TabType>('overview')
  const [formData, setFormData] =
    useState<IIncidentReportFormData>(DEFAULT_FORM_DATA)
  const [reportData, setReportData] = useState<any>(null)
  const [hasDuplicateReport, setHasDuplicateReport] = useState(false)

  const isEditMode = Boolean(incidentReportObjectId)

  useEffect(() => {
    if (isOpen && incidentReportObjectId) {
      loadIncidentReport()
    } else if (isOpen && !incidentReportObjectId) {
      initializeNewReport()
    }
  }, [isOpen, incidentReportObjectId, orderObjectId, orderData, defaultValues])

  // Check for existing incident reports when opening the modal for a new report
  const effectiveOrderObjectId = orderObjectId || orderData?.objectId
  useEffect(() => {
    if (!isOpen || isEditMode || !effectiveOrderObjectId) return

    let cancelled = false
    async function checkForExistingReports() {
      try {
        const existingReports = await fetchData<{ objectId: string }[]>(
          getIncidentReportsByOrderObjectId(effectiveOrderObjectId!),
        )
        if (!cancelled && existingReports && existingReports.length > 0) {
          setHasDuplicateReport(true)
          const woId = orderData?.workOrderId || effectiveOrderObjectId
          setToastStatus({
            isOpen: true,
            message: `Incident Report already created for WO #${woId}`,
            severity: 'error',
          })
        }
      } catch (error) {
        console.error('Error checking existing incident reports:', error)
      }
    }
    checkForExistingReports()
    return () => { cancelled = true }
  }, [isOpen, isEditMode, effectiveOrderObjectId])

  async function initializeNewReport() {
    // Clear any previous report data
    setReportData(null)

    // Get mileage from order's first vehicle if available
    const orderVehicleMileage = orderData?.orderVehicles?.[0]?.mileage ?? null

    const newFormData: IIncidentReportFormData = {
      ...DEFAULT_FORM_DATA,
      orderObjectId: orderObjectId || orderData?.objectId || '',
      vanObjectId: orderData?.van?.objectId || '',
      customerMileageAtIncident: orderVehicleMileage,
      workOrderId: orderData?.workOrderId?.toString() || '',
      // Apply default values from case if provided
      ...(defaultValues?.cost !== undefined && { cost: defaultValues.cost }),
      ...(defaultValues?.incidentOverviewAndResolution && {
        incidentOverviewAndResolution: defaultValues.incidentOverviewAndResolution,
      }),
      ...(defaultValues?.wheelOff !== undefined && { wheelOff: defaultValues.wheelOff }),
    }

    // Fetch technician assigned to order if we have order data
    if (orderData?.objectId) {
      try {
        const technician = await fetchData<ITechnician | null>(
          getTechnicianAssignedToOrder(orderData.objectId),
        )
        if (technician) {
          newFormData.technicianObjectId = technician.objectId
        }
      } catch (error) {
        console.error('Error fetching technician:', error)
      }
    }

    setFormData(newFormData)
  }

  async function loadIncidentReport() {
    try {
      setIsLoading(true)
      const response = await fetchData<any>(
        getIncidentReportByObjectId(incidentReportObjectId!),
      )

      if (response) {
        setReportData(response)

        setFormData({
          objectId: response.objectId,
          incidentDate: response.incidentDate
            ? new Date(response.incidentDate).toISOString().split('T')[0]
            : '',
          technicianObjectId: response.technician?.objectId,
          orderObjectId: response.order?.objectId,
          vanObjectId: response.van?.objectId,
          installationPointObjectId: response.installationPoint?.objectId,
          coachObjectId: response.coach?.objectId,
          isLostTimeInjury: response.isLostTimeInjury || false,
          reviewChannel: response.reviewChannel || '',
          reviewScore: response.reviewScore,
          incidentType: response.incidentType || '',
          incidentOverviewAndResolution:
            response.incidentOverviewAndResolution || '',
          severityOfIncident: response.severityOfIncident || '',
          investigationSummary: response.investigationSummary || '',
          employeeStatement: response.employeeStatement || '',
          cost: response.cost,
          qualifyingDefects: response.qualifyingDefects || false,
          dtClaims: response.dtClaims || false,
          dtClaimsNumber: response.dtClaimsNumber || '',
          wheelOff: response.wheelOff || false,
          wheelOffReason: response.wheelOffReason || '',
          isAccident: response.isAccident || false,
          accidentData: response.accidentData || DEFAULT_FORM_DATA.accidentData,
          rootCauseAnalysis: response.rootCauseAnalysis || '',
          recommendations: response.recommendations || '',
          correctionActionPlan: response.correctionActionPlan || '',
          customerMileageAtIncident: response.customerMileageAtIncident,
          selectedImageObjectIds: [],
          workOrderId: response.order?.workOrderId?.toString() || '',
          status: response.status || '',
        })
      }
    } catch (error) {
      console.error('Error loading incident report:', error)
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'Error loading incident report',
        severity: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }

  async function handleSave() {
    try {
      setIsSaving(true)

      const payload = {
        ...formData,
        incidentDate: formData.incidentDate
          ? new Date(formData.incidentDate)
          : null,
      }

      if (isEditMode) {
        await fetchData(updateIncidentReport(payload))
        setToastStatus({
          ...toastStatus,
          isOpen: true,
          message: 'Incident report updated successfully',
          severity: 'success',
        })
      } else {
        await fetchData(createIncidentReport(payload))
        setToastStatus({
          ...toastStatus,
          isOpen: true,
          message: 'Incident report created successfully',
          severity: 'success',
        })
      }

      onSaveSuccess?.()
      handleClosed()
    } catch (error) {
      console.error('Error saving incident report:', error)
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: `Error ${isEditMode ? 'updating' : 'creating'} incident report`,
        severity: 'error',
      })
    } finally {
      setIsSaving(false)
    }
  }

  function handleClose() {
    setFormData(DEFAULT_FORM_DATA)
    setReportData(null)
    setHasDuplicateReport(false)
    setActiveTab('overview')
    handleClosed()
  }

  // Check if we have order data to show the side panel
  const hasOrderData = Boolean(orderData?.objectId || reportData?.order?.objectId)

  async function handleExportPdf() {
    if (!reportData || !formData.objectId) {
      return
    }

    try {
      // Fetch incident report uploaded images as base64 (backend converts to avoid CORS)
      const incidentImages = await fetchData<{ objectId: string; base64: string; type: string }[]>(
        getIncidentReportImagesAsBase64(formData.objectId),
      ) || []

      // Fetch selected installation photos as base64
      let selectedInstallationImages: { objectId: string; base64: string; type: string }[] = []
      const orderVehicleObjectId =
        reportData?.order?.orderVehicles?.[0]?.objectId ||
        orderData?.orderVehicles?.[0]?.objectId

      if (orderVehicleObjectId && formData.selectedImageObjectIds.length > 0) {
        const allOrderImages = await fetchData<{ objectId: string; base64: string; type: string }[]>(
          getOrderImagesAsBase64(orderVehicleObjectId),
        ) || []

        selectedInstallationImages = allOrderImages.filter((img) =>
          formData.selectedImageObjectIds.includes(img.objectId),
        )
      }

      const allImages = [...selectedInstallationImages, ...incidentImages]

      await generateIncidentReportPdf(reportData, formData, allImages, orderData)
    } catch (error) {
      console.error('Error exporting PDF:', error)
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'Error exporting incident report PDF',
        severity: 'error',
      })
    }
  }

  const tabs: { key: TabType; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'investigation', label: 'Investigation' },
    { key: 'rootCause', label: 'Root Cause' },
    { key: 'recommendations', label: 'Recommendations' },
    { key: 'actionPlan', label: 'Action Plan' },
  ]

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      aria-labelledby='incident-report-modal-title'
    >
      <Box className={styles.incidentReportModal}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>
            {isEditMode ? 'Edit Incident Report' : 'Create Incident Report'}
          </div>
        </div>

        <div className={styles.tabsContainer}>
          {tabs.map((tab) =>
            activeTab === tab.key ? (
              <PrimaryButton
                key={tab.key}
                buttonName={tab.label}
                onClick={() => setActiveTab(tab.key)}
                style={TAB_BUTTON_STYLE}
              />
            ) : (
              <SecondaryButton
                key={tab.key}
                buttonName={tab.label}
                onClick={() => setActiveTab(tab.key)}
                style={TAB_BUTTON_STYLE}
              />
            ),
          )}
        </div>

        {isLoading ? (
          <div className={styles.progressContainer}>
            <CircularProgress />
          </div>
        ) : (
          <div className={hasOrderData ? styles.contentWrapper : styles.tabContent}>
            <div className={hasOrderData ? styles.mainContent : undefined}>
              {activeTab === 'overview' && (
                <OverviewTab
                  formData={formData}
                  setFormData={setFormData}
                />
              )}
              {activeTab === 'investigation' && (
                <InvestigationTab
                  formData={formData}
                  setFormData={setFormData}
                  orderVehicleObjectId={orderData?.orderVehicles?.[0]?.objectId || reportData?.order?.orderVehicles?.[0]?.objectId}
                  incidentReportObjectId={formData.objectId}
                />
              )}
              {activeTab === 'rootCause' && (
                <RootCauseTab formData={formData} setFormData={setFormData} />
              )}
              {activeTab === 'recommendations' && (
                <RecommendationsTab formData={formData} setFormData={setFormData} />
              )}
              {activeTab === 'actionPlan' && (
                <ActionPlanTab formData={formData} setFormData={setFormData} />
              )}
            </div>
            {hasOrderData && (
              <OrderDetailsPanel
                orderData={orderData}
                reportData={reportData}
              />
            )}
          </div>
        )}

        <div className={styles.modalFooter}>
          <SecondaryButton buttonName='Close' onClick={handleClose} />
          <PrimaryButton
            buttonName='Export as PDF'
            onClick={handleExportPdf}
            disabled={!reportData || isLoading}
          />
          <PrimaryButton
            buttonName={isSaving ? 'Saving...' : 'Save'}
            onClick={handleSave}
            disabled={isSaving || isLoading || (!isEditMode && hasDuplicateReport)}
          />
        </div>
      </Box>
    </Modal>
  )
}

export default IncidentReportModal
